import { useState, useEffect, useCallback, FC, useMemo } from 'react';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import * as Store from '@apply/store';
import { useAuthentication } from '../LoginPage';
import { Header } from '../Header';
import { Acknowledgement } from './Acknowledgement';
import { PaymentInfo } from './PaymentInfo';
import { AppraisalInvoice } from './AppraisalInvoice';
import { CardInfo, alertModal, loginAlertModal } from '@apply/ui';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { BorrowerStore } from '@apply/store';
import { FormValidationMetadata } from '@apply/ui';
import './appraisalpage.scss';
import { LoanType, paymentAgreement, PaymentReason } from '@apply/constants';
import { BrokerClientApi, PaymentApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';

const emptyBillingDetails: PaymentApi.BillingDetails = {
    address: {
        city: '',
        country: 'US',
        line1: '',
        line2: '',
        postal_code: '',
        state: '',
    },
    email: '',
    name: '',
    phone: '',
};

export interface Actions {
    loadStateFromApi?: (apiId: string, token: string) => Promise<boolean | undefined>;
    saveStateToApi?: (apiId: string, complete: boolean) => Promise<Store.BorrowerStore.Types.IdAndToken>;
    reset?: () => void;
}

export interface AppraisalPageProps {
    apiId: string;
    invoice: BorrowerStore.Types.InvoiceLine[];
    showLocTerms: boolean;
}

export const AppraisalPage: FC<AppraisalPageProps> = ({ apiId, invoice, showLocTerms }) => {
    const navigate = useNavigate();

    const paymentGuid = useMemo(() => uuidv4(), []);

    const [billingDetails, setBillingDetails] = useState<PaymentApi.BillingDetails>(emptyBillingDetails);
    const [validation, setValidation] = useState<FormValidationMetadata>({});
    const [submitted, setSubmitted] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [paymentLoading, setPaymentLoading] = useState(false);

    const [maintenanceModeOn] = useState<boolean>(false);
    const [cardInfo, set_cardInfo] = useState<CardInfo>({
        cvc: '',
        expiry: '',
        number: '',
        cardType: undefined,
    });

    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);
    const paymentApi = new PaymentApi.PaymentApi(applicationInsights);

    const onQuestionValidate = useCallback((selector: string, passing: boolean, message: string) => {
        setValidation((state) => {
            const record = {
                ...state[selector],
                passing,
                failureMessage: message,
            };
            return {
                ...state,
                [selector]: record,
            };
        });
    }, []);

    const onChangeBillingDetails = (newValue: string, propertyName: string) => {
        setBillingDetails((previous) => ({
            ...previous,
            [propertyName]: newValue,
        }));
    };

    const onChangeBillingAddress = (newValue: string, propertyName: string) => {
        setBillingDetails((previous) => ({
            ...previous,
            address: {
                ...previous.address,
                [propertyName]: newValue,
            },
        }));
    };

    const total = invoice ? invoice.reduce((cost, listing) => cost + listing.cost + listing.underwritingFee, 0) : 0;
    useAuthentication();

    const payButtonClicked = async () => {
        setPaymentError('');
        setPaymentLoading(true);

        let address = billingDetails.address.line1;
        if (billingDetails.address.line2) {
            address = `${address} ${billingDetails.address.line2}`;
        }

        const expirationTokens = cardInfo.expiry.split('/');

        const addressRequest: PaymentApi.AddressVerificationRequest = {
            paymentGuid: paymentGuid,
            cardNumber: cardInfo.number.replace(/ /g, ''),
            creditCardType: cardInfo.cardType,
            expirationDate: `20${expirationTokens[1]}-${expirationTokens[0]}`,
            creditDebitType: 'CREDIT',
            address: address,
            zipCode: billingDetails.address.postal_code,
            amount: total,
        };

        const addressResponse = await paymentApi.verifyAddress(addressRequest);

        if (addressResponse.statusCode !== 200) {
            setPaymentLoading(false);
            setPaymentError(addressResponse.errors[0]);
            return;
        }

        const response = await brokerClientApi.getIpAddress();
        if (response.statusCode !== 200) {
            setPaymentLoading(false);
            setPaymentError('Could not calculate IP address.');
            return;
        }
        const ipAddress = response.result;

        const authorizationRequest: PaymentApi.AuthorizationRequest = {
            paymentGuid: paymentGuid,
            accountNumber: cardInfo.number.replace(/ /g, ''),
            name: billingDetails.name,
            phone: billingDetails.phone,
            email: billingDetails.email,
            address1: billingDetails.address.line1,
            address2: billingDetails.address.line2,
            city: billingDetails.address.city,
            state: billingDetails.address.state,
            zip: billingDetails.address.postal_code,
            cardType: cardInfo.cardType,
            expirationDate: `20${expirationTokens[1]}-${expirationTokens[0]}`,
            amount: total,
            avsAuthResponseCode: addressResponse.result.avsResponseCode,
            paymentReason: PaymentReason.Appraisal,
            memo: `Appraisal payment for ${invoice[0].address1} ${invoice[0].address2}`,
            applicationId: apiId,
            disclaimer: paymentAgreement,
            ipAddress: ipAddress,
        };

        const authorizationResponse = await paymentApi.authorization(authorizationRequest);

        if (authorizationResponse.statusCode !== 200) {
            setPaymentLoading(false);
            setPaymentError('Payment failed - Please try again or contact BFI.');
            return;
        }

        if (!authorizationResponse.result.isSuccess) {
            setPaymentLoading(false);
            setPaymentError(authorizationResponse.result.errorMessage);
            return;
        }

        setPaymentLoading(false);
        setPaymentError('');
        navigate('/thankyou');
    };

    const addressSelectors = {
        name: 'payerName',
        phone: 'payerPhone',
        email: 'payerEmail',
        city: 'billingAddressCity',
        state: 'billingAddressState',
        address1: 'billingStreetAddressLine1',
        address2: 'billingStreetAddressLine2',
    };

    const payButtonDisabled = () => {
        const notSupportedCardType = cardInfo.cardType != 'VISA' && cardInfo.cardType != 'MASTERCARD';

        if (notSupportedCardType) return true;

        const addressValid = !Object.keys(addressSelectors).some((key) => {
            const selector = addressSelectors[key];
            return validation[selector]?.passing == false;
        });
        return !(acknowledged && addressValid && !paymentLoading);
    };

    return (
        <>
            <Header />
            <main>
                <div className='appraisal-page'>
                    <AppraisalInvoice
                        invoice={invoice}
                        total={total}
                    />

                    {maintenanceModeOn && (
                        <>
                            <div
                                style={{
                                    border: '1px solid',
                                    background: '#F9F9F9',
                                    borderRadius: '6px',
                                    textAlign: 'center',
                                }}
                            >
                                <h3>Payment Processing is Currently Under Maintenance.</h3>
                                <div style={{ padding: '10px' }}>This functionality will be restored Tuesday, March 5, 2024.</div>
                                <div
                                    style={{
                                        paddingTop: '10px',
                                        paddingBottom: '20px',
                                    }}
                                >
                                    Please contact BFI for any payment questions at:{' '}
                                    <a href='mailto:team@builderfinance.com?subject=Payments'>team@builderfinance.com</a> with the
                                    subject line "Payments".
                                </div>
                            </div>
                        </>
                    )}
                    {!maintenanceModeOn && (
                        <>
                            <PaymentInfo
                                onQuestionValidate={onQuestionValidate}
                                onChangeBillingDetails={onChangeBillingDetails}
                                onChangeBillingAddress={onChangeBillingAddress}
                                submitted={submitted}
                                billingDetails={billingDetails}
                                addressSelectors={addressSelectors}
                                paymentError={paymentError}
                                cardInfo={cardInfo}
                                set_cardInfo={set_cardInfo}
                            />
                            <Acknowledgement
                                setAcknowledged={setAcknowledged}
                                acknowledged={acknowledged}
                                showLocTerms={showLocTerms}
                            />
                            <div
                                className='button-group'
                                onMouseEnter={() => setSubmitted(true)}
                            >
                                <button
                                    id='payButton'
                                    className='button'
                                    onClick={payButtonClicked}
                                    disabled={payButtonDisabled()}
                                >
                                    Pay
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </>
    );
};

export type AppraisalWithStripeProps = {
    state?: Store.ApplyRootStateType;
    actions?: Actions;
};

export const AppraisalWithStripe: FC<AppraisalWithStripeProps> = (props) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string>('');
    const [invoice, setInvoice] = useState<BorrowerStore.Types.InvoiceLine[]>([]);
    const thisLocation = useLocation();
    const navigate = useNavigate();
    const { logout } = useAuthentication();
    const { apiId } = useParams<{ apiId: string }>();
    const [showLocTerms, setShowLocTerms] = useState<boolean>(false);

    useEffect(() => {
        if (!apiId) {
            return;
        }
        (async () => {
            setShowLocTerms(props.state.borrower.loanType === LoanType.SubNoteConstruction);
            props?.actions?.reset();

            setLoading(true);
            let appraisalInvoice: Store.BorrowerStore.Types.AppraisalInvoice;
            try {
                appraisalInvoice = await Store.CrossStore.getAppraisalInvoice(apiId);
            } catch (err) {
                console.error(err);
                setLoadError(err);
            }
            setInvoice(appraisalInvoice?.invoiceLines || []);
            setLoading(false);
        })();
    }, [apiId]);

    useEffect(() => {
        if (loadError) {
            if (String(loadError).includes('401 - User not Authorized.')) {
                loginAlertModal({
                    onConfirm: () => {
                        setLoadError(undefined);
                        logout();
                        navigate(`/login?redirectTo=${thisLocation.pathname}`);
                    },
                });
            } else {
                alertModal({
                    message: 'An error has occurred while communicating with our server.  Please try your operation again.',
                    onConfirm: () => {
                        setLoadError(undefined);
                        navigate(-1);
                    },
                });
            }
        }
    }, [loadError, logout, thisLocation.pathname]);

    if (loading) {
        return (
            <div
                className='overlay'
                data-testid='review-page-loading'
            >
                <div className='overlay__inner'>
                    <div className='overlay__content'>
                        <span className='spinner'></span>
                        <span className='spinner-text'>Loading</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <AppraisalPage
            {...props}
            apiId={apiId}
            invoice={invoice}
            showLocTerms={showLocTerms}
        />
    );
};

export const mapStateToProps = (state: Store.ApplyRootStateType) => ({
    state,
});

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            loadStateFromApi: (apiId: string, token: string) => dispatch(Store.CrossStore.loadStateFromApi(apiId, null, token)),
            saveStateToApi: (apiId: string, complete = false) => dispatch(Store.CrossStore.saveStateToApi(apiId, complete)),
            reset: () => {
                dispatch(Store.BorrowerStore.Actions.reset());
                dispatch(Store.PropertiesStore.Actions.reset());
                dispatch(Store.FormsStore.Actions.reset());
            },
        },
        dispatch,
    };
    return props;
};
export default connect(mapStateToProps, mapDispatchToProps)(AppraisalWithStripe);
